// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useState } from 'react';
import { Box, Divider, Tab, Tabs } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { RejectDialog } from '../../../components/Dialog/RejectDialog';
import Loading from '../../../components/Loading';
import { MoreActionsMenu } from '../../../components/MoreActionsMenu';
import PartyDisplay from '../../../components/PartyDisplay';
import { StateCell, Status } from '../../../components/StateCell';
import { DataTable } from '../../../components/Table/DataTable';
import { useCollateralUserService } from '../../../hooks/queries/collateral/onboarding/useUserService';
import { PageWrapper } from '../../PageWrapper';
import { OfferCollateralDialog } from '../collaterals/OfferCollateralDialog';
import { Positions } from '../collaterals/Positions';
import { CollateralAgreementRow, GenericAction } from '../collaterals/types';
import { AgreementDetailsDialog } from './AgreementDetailsDialog';
import { AgreementTerminateDialog } from './AgreementTerminateDialog';
import { MemoizedRequestCollateralAgreementDialog } from './RequestCollateralAgreementDialog';
import { SettlementHistoryDialog } from './SettlementHistoryDialog';
import { useCreateAgreementActions } from './useCreateAgreementActions';
import { useGetAgreementRows } from './useGetAgreementRows';

type Action = GenericAction<CollateralAgreementRow>;

const createHeaders: (
  createActions: (r: CollateralAgreementRow) => Action[],
  setSelectedRow: (r: CollateralAgreementRow) => void,
) => GridColDef<CollateralAgreementRow>[] = (createActions, setSelectedRow) => {
  const renderActionCell = (params: GridRenderCellParams<CollateralAgreementRow>) => {
    const actions = createActions(params.row);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        {actions.map(({ name, action, buttonProps, inMoreMenu }) =>
          inMoreMenu ? null : (
            <AsyncButton
              variant={buttonProps?.variant}
              color={buttonProps?.color}
              key={name}
              sx={{ mr: 1 }}
              onClick={async () => action(params.row)}
            >
              {name}
            </AsyncButton>
          ),
        )}
        <MoreActionsMenu
          setSelectedRow={setSelectedRow}
          row={params.row}
          actions={actions.filter((a) => a.inMoreMenu === true)}
        />
      </Box>
    );
  };

  return [
    {
      field: 'state',
      headerName: 'STATE',
      renderCell: (params) => <StateCell state={params.row.state as Status} />,
      flex: 0.5,
      filterable: true,
      valueGetter: (params) => params.row.state,
    },
    {
      field: 'id',
      headerName: 'Agreement Id',
      valueGetter: (params) => params.row.payload.id,
      flex: 1,
      filterable: true,
    },
    {
      field: 'partyA',
      headerName: 'Party A',
      renderCell: (params) => <PartyDisplay party={params.row.payload.partyA} />,
      flex: 1,
      filterable: true,
      valueGetter: (params) => params.row.payload.partyA,
    },
    {
      field: 'partyB',
      headerName: 'Party B',
      renderCell: (params) => <PartyDisplay party={params.row.payload.partyB} />,
      flex: 1,
      filterable: true,
      valueGetter: (params) => params.row.payload.partyB,
    },
    {
      field: 'total balance',
      headerName: 'total balance',
      valueGetter: (params) => {
        return params.row.payload.collateralPositions ? (
          <Positions positions={params.row.payload.collateralPositions} />
        ) : null;
      },
      flex: 1,
      filterable: false,
    },
    {
      field: ' ',
      sortable: false,
      align: 'right',
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => renderActionCell(params),
      filterable: false,
    },
  ];
};

export const Agreements = () => {
  const userService = useCollateralUserService();
  const { isLoading: isRowLoading, rows } = useGetAgreementRows();
  const [selectedRow, setRow] = React.useState<CollateralAgreementRow | undefined>();
  const setSelectedRow = (r: CollateralAgreementRow) => {
    setRow((p) => {
      if (p?.contractId !== r.contractId) {
        return r;
      }
      return p;
    });
  };
  const { onReject, setOnReject, createCollateralActions, onTerminate, dialogName, setDialogName } =
    useCreateAgreementActions();
  const isLoading = userService.isLoading || isRowLoading || !userService.data;
  const [requestAgreementIsOpen, setRequestAgreementIsOpen] = useState(false);
  const [tabIndex, setTabIndex] = React.useState(0);
  const onChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };
  const closeDialog = React.useCallback(() => {
    setDialogName('');
  }, [setDialogName]);
  if (isLoading) return <Loading />;
  const isUser = !!userService.data;
  const displayedRows = rows.filter((r) => {
    if (tabIndex === 1) {
      return r.state === 'Accepted';
    }
    if (tabIndex === 2) {
      return r.state === 'Pending';
    }
    return r;
  });
  return (
    <PageWrapper
      buttonLabel="Create agreement"
      pageName="Agreements"
      onClick={() => {
        setRequestAgreementIsOpen(true);
      }}
    >
      <Tabs value={tabIndex} onChange={onChange}>
        <Tab sx={{ textTransform: 'capitalize' }} value={0} label="All" />
        <Tab sx={{ textTransform: 'capitalize' }} value={2} label="Pending" />
        <Tab sx={{ textTransform: 'capitalize' }} value={1} label="Active" />
      </Tabs>
      <Divider />

      {isUser && requestAgreementIsOpen && (
        <MemoizedRequestCollateralAgreementDialog
          open
          onClose={() => setRequestAgreementIsOpen(false)}
        />
      )}
      {isUser && dialogName === 'modify' && (
        <MemoizedRequestCollateralAgreementDialog open row={selectedRow} onClose={closeDialog} />
      )}

      <RejectDialog
        disableReason
        open={!!onReject}
        onClose={() => setOnReject(undefined)}
        onReject={onReject}
      />
      <AgreementDetailsDialog
        selectedRow={selectedRow}
        open={dialogName === 'details'}
        onClose={() => setDialogName('')}
      />
      <OfferCollateralDialog
        collateralAgreementPayload={selectedRow}
        open={dialogName === 'instructCollateral'}
        onClose={() => setDialogName('')}
      />
      <SettlementHistoryDialog
        selectedRow={selectedRow}
        open={dialogName === 'history'}
        onClose={() => setDialogName('')}
      />
      <AgreementTerminateDialog
        hasPositions={!!selectedRow?.payload.collateralPositions?.length}
        open={dialogName === 'terminate'}
        onClose={() => setDialogName('')}
        onTerminate={onTerminate}
      />
      <DataTable
        columns={createHeaders(createCollateralActions, setSelectedRow)}
        rows={displayedRows}
        rowKey={(a) => a.contractId}
      />
    </PageWrapper>
  );
};
